import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simular envío (aquí conectarías con tu API)
    setTimeout(() => {
      toast.success('Mensaje enviado correctamente. Te responderemos pronto!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      setLoading(false);
    }, 1500);
  };

  const whatsappNumber = '5492664726581'; // +54 9 2664 72-6581
  const whatsappMessage = encodeURIComponent('Hola! Me gustaría hacer una consulta sobre...');

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-[#2d3c5d] mb-4">Contacto</h1>
          <p className="text-gray-600">
            ¿Tenés alguna consulta o necesitás ayuda? Estamos para ayudarte. Escribinos y te responderemos a la brevedad.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Información de contacto */}
          <div className="lg:col-span-1 space-y-6">
            {/* Email */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#2d3c5d]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2d3c5d] mb-2">Email</h3>
                  <a
                    href="mailto:info@decohomesinrival.com.ar"
                    className="text-gray-700 hover:text-[#2d3c5d] transition"
                  >
                    info@decohomesinrival.com.ar
                  </a>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2d3c5d] mb-2">WhatsApp</h3>
                  <a
                    href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 hover:text-green-600 transition"
                  >
                    +54 9 2664 72-6581
                  </a>
                  <a
                    href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Enviar mensaje
                  </a>
                </div>
              </div>
            </div>

            {/* Horarios */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#2d3c5d]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2d3c5d] mb-2">Horario de atención</h3>
                  <div className="text-gray-700 text-sm space-y-1">
                    <p>Lunes a Viernes</p>
                    <p className="font-semibold">09:00 - 17:00 hs</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Respondemos consultas dentro de las 72 hs hábiles
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ubicación */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#2d3c5d]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2d3c5d] mb-2">Ubicación</h3>
                  <p className="text-gray-700 text-sm font-semibold">
                    Ruta 3 y Salvador Segado
                  </p>
                  <p className="text-gray-700 text-sm">
                    San Luis, Argentina
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Venta online con envíos a todo el país
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Formulario de contacto */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-[#2d3c5d] mb-6">
                Envíanos un mensaje
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                      placeholder="Tu teléfono"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Asunto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                    >
                      <option value="">Seleccioná un asunto</option>
                      <option value="consulta">Consulta general</option>
                      <option value="pedido">Consulta sobre un pedido</option>
                      <option value="producto">Consulta sobre un producto</option>
                      <option value="cambio">Cambio o devolución</option>
                      <option value="pago">Problemas con el pago</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                    placeholder="Contanos en qué podemos ayudarte..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-white transition disabled:opacity-50"
                  style={{ backgroundColor: '#2d3c5d' }}
                >
                  {loading ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar mensaje
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Botón volver */}
        <div className="mt-8">
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-lg font-semibold text-white transition"
            style={{ backgroundColor: '#2d3c5d' }}
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Contact;
